/**
 * Pipeline feature flags. Each step can be enabled/disabled independently so
 * future parsing logics (fuzzy match, etc.) plug in without rewriting the core.
 *
 * Enabled by default first, then opt-in / future steps.
 */
export interface ParserPipelineFlags {
  /**
   * Step 1: property type, beds/baths/parking, price, sqft, features, rental,
   * status, locker, maintenance fee, hasImage, showComingSoon.
   */
  extractFilters: boolean;
  /** Sort phrases ("cheapest first", "newest first"). */
  sortExtraction: boolean;
  /** Relative dates → Active Date Since/Until (`listedSince` / `listedTo`). */
  relativeDate: boolean;
  /** Location via Go-Search predictions (city / neighbourhood / province / country). */
  locationIdentification: boolean;
  /**Resolve leftover residual tokens via Google Places Autocomplete */
  googlePlacePredictions: boolean;
  /** Step 2: confidence scores on extracted values. */
  confidence: boolean;
  /**
   * When true, the smart-search LLM prompt asks the model to assign tags from
   * the canonical smart-search tag list. Rides along on a call `aiFallback`
   * was already making — it never causes one, so a query the extractors
   * understood completely comes back with no tags.
   */
  generateTags: boolean;
  /**
   * When true, leftover residual (or an empty deterministic parse) is sent to
   * the LLM to infer additional filters (and tags when `generateTags` is on).
   */
  aiFallback: boolean;
  /** Future: fuzzy / typo-tolerant matching. */
  fuzzyMatch: boolean;
}

/**
 * What the pipeline does unless a caller says otherwise. Flip a flag here to
 * change the default for every caller.
 */
export const DEFAULT_PARSER_FLAGS: Readonly<ParserPipelineFlags> = {
  extractFilters: true,
  sortExtraction: true,
  relativeDate: true,
  locationIdentification: true,
  googlePlacePredictions: false,
  confidence: true,
  generateTags: true,
  aiFallback: true,
  fuzzyMatch: false,
};

/**
 * The flags a parse runs under: the defaults above, with whatever the caller
 * turned off.
 *
 * A step is only worth running when the caller has no better answer of its own,
 * and that differs by caller: a chat surface has nothing but the query, while a
 * search box already resolves places through its own predictions dropdown and
 * would only be asking the same service the same question again. So the caller
 * names the steps it does not need, and the parser stays the single place that
 * decides what each step means.
 */
export function resolveParserFlags(
  overrides?: Partial<ParserPipelineFlags>
): ParserPipelineFlags {
  return { ...DEFAULT_PARSER_FLAGS, ...overrides };
}
